import { useState } from "react";
import type { FormEvent } from "react";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/dahhup3p14a1h3mr6ncg";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("提交成功，感谢你的分享，我们会尽快查看。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string") payload.append(key, value);
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("提交成功，感谢你的分享，我们会尽快查看。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后再试。");
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors";

  return (
    <form
      data-readdy-form
      id="heiliao-contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
      noValidate={false}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-bold text-foreground-800 mb-2">
            称呼 <span className="text-accent-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="怎么称呼你"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-bold text-foreground-800 mb-2">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            maxLength={80}
            placeholder="用于接收我们的回复"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="topic_type" className="block text-sm font-bold text-foreground-800 mb-2">
            线索类型
          </label>
          <select id="topic_type" name="topic_type" className={inputClass} defaultValue="综艺幕后">
            <option value="综艺幕后">综艺幕后</option>
            <option value="影视探班">影视探班</option>
            <option value="剧组日常">剧组日常</option>
            <option value="剪辑与后期">剪辑与后期</option>
            <option value="拍摄现场">拍摄现场</option>
            <option value="合作与其他">合作与其他</option>
          </select>
        </div>
        <div>
          <label htmlFor="contact_way" className="block text-sm font-bold text-foreground-800 mb-2">
            方便的联系方式
          </label>
          <input
            id="contact_way"
            name="contact_way"
            type="text"
            maxLength={60}
            placeholder="微信 / 电话 / 其他（选填）"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="message" className="block text-sm font-bold text-foreground-800 mb-2">
          线索或想说的话 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请简单描述你了解的幕后情况、涉及的作品类型与大致阶段，以及你愿意公开的信息范围（不超过 500 字）。"
          className={`${inputClass} resize-none`}
        />
        <p className="text-xs text-foreground-500 mt-2">最多 500 字，我们会逐条确认可公开的细节。</p>
      </div>

      {/* 辅助字段 */}
      <input
        className="aux-field-noop"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex items-center justify-center gap-1.5 w-full md:w-auto px-8 py-3 rounded-md bg-primary-500 text-foreground-950 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-primary-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
        {status === "submitting" ? "提交中…" : "提交线索"}
      </button>

      {status === "success" ? (
        <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary-700 font-bold">
          <i className="ri-checkbox-circle-fill w-4 h-4 flex items-center justify-center" />
          {feedback}
        </p>
      ) : null}
      {status === "error" ? (
        <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent-700 font-bold">
          <i className="ri-error-warning-fill w-4 h-4 flex items-center justify-center" />
          {feedback}
        </p>
      ) : null}
    </form>
  );
}